/**
 * v1 → v2 migration switch flags (build-time constants, D3D-865).
 *
 * `true` makes the group call v2-server instead of the legacy v1 server.
 * The same image is promoted dev → stg → prod, so every environment gets the
 * same values; roll back by flipping a flag back to `false` and rebuilding.
 */
export const V2_MIGRATION = {
	// High topics (docs/tasks/v1-v2-migration/high-design.md)
	auth: true, // D3D-875 login
	crossSection: true, // D3D-876 cross-section DXF
	roadAi: false, // D3D-877 temporary road AI chat
	coord: false, // D3D-879, D3D-881 coordinate conversion
	lockObject: false, // D3D-885 object lock and object CRUD
	site: false, // D3D-888 site coordinate system and bucket init
	// Remaining routes (A1-A17)
	twoPointsPicker: false, // D3D-893
	arbitraryLinear: false, // D3D-894
	simaLinear: false, // D3D-895
	crossSectionCommon: false, // D3D-896
	pointCloudList: false, // D3D-897
	pointCloudHeightmap: false, // D3D-898
	designList: false, // D3D-899
	drawing2dImage: false, // D3D-900
	drawing2dImageImport: false, // D3D-901
	drawing2dImageExport: false, // D3D-902
	lineworks: false, // D3D-903
	lineworksImport: false, // D3D-904
	linearFileImportExport: false, // D3D-905
	assetLinkBrowse: false, // D3D-906
	dashboard: false, // D3D-907
	siteList: false, // D3D-908
	startup: true, // D3D-909
} as const satisfies Record<string, boolean>;

export type V2MigrationGroup = keyof typeof V2_MIGRATION;

/**
 * debugFlag キー（`window.debugFlag.v2_<group>`）。true / false でこの画面だけ
 * そのグループを v2 / v1 に切り替える。保存しないので再読込で消え、ビルド時の値に戻る。
 */
export const migrationDebugFlagKey = (group: V2MigrationGroup): string =>
	`v2_${group}`;

/** グループが v2 か。`window.debugFlag.v2_<group>` があればそれ、無ければビルド時の値 */
export const isMigratedToV2 = (group: V2MigrationGroup): boolean =>
	globalThis.window?.debugFlag?.[migrationDebugFlagKey(group)] ??
	V2_MIGRATION[group];

/** Picks the v1 or v2 implementation for a migration group (debugFlag override first). */
export const selectByMigration = <T>(
	group: V2MigrationGroup,
	impl: Readonly<{ v1: T; v2: T }>,
): T => (isMigratedToV2(group) ? impl.v2 : impl.v1);

/**
 * Builds the login entry URL: v2 `/v2/auth/login` or legacy v1 `/login`.
 * `VITE_API_V2_BASE` includes `/v2/api`, so it is stripped to get the v2 root.
 */
export const buildLoginUrl = (
	useV2: boolean,
	env: Readonly<{ apiBase?: string; apiV2Base?: string }>,
): string => {
	// Same template as the former inline call sites, so v1 behaviour is unchanged.
	if (!useV2) return `${env.apiBase}/login`;
	const v2Root = (env.apiV2Base ?? "").replace(/\/v2\/api\/?$/, "");
	return `${v2Root}/v2/auth/login`;
};

/** Login entry URL for the current build's `auth` flag. */
export const getLoginUrl = (): string =>
	buildLoginUrl(isMigratedToV2("auth"), {
		apiBase: import.meta.env.VITE_API_BASE as string | undefined,
		apiV2Base: import.meta.env.VITE_API_V2_BASE as string | undefined,
	});
